/**
 * MotionCard — spring hover-lift card with tap scale feedback.
 *
 * Replaces CSS `.card-lift` / `hover:shadow-lg` patterns with proper spring physics.
 *
 * Usage:
 *   <MotionCard onClick={...} className="rounded-2xl bg-white p-4">
 *     <ProductCard />
 *   </MotionCard>
 *
 * Respects prefers-reduced-motion automatically (framer-motion built-in).
 */
'use client';

import { motion } from 'framer-motion';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { cn } from './utils';

interface MotionCardProps extends ComponentPropsWithoutRef<'div'> {
  children: ReactNode;
  /** Lift scale on hover (default 1.02) */
  hoverScale?: number;
  /** Tap scale (default 0.98) */
  tapScale?: number;
  /** Whether to show an elevated shadow on hover */
  elevate?: boolean;
  /** Disable hover/tap — useful for loading states */
  disabled?: boolean;
}

export function MotionCard({
  children,
  hoverScale = 1.02,
  tapScale = 0.98,
  elevate = true,
  disabled = false,
  className,
  ...props
}: MotionCardProps) {
  return (
    <motion.div
      whileHover={disabled ? undefined : { scale: hoverScale, y: elevate ? -2 : 0 }}
      whileTap={disabled ? undefined : { scale: tapScale }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      className={cn('cursor-pointer', className)}
      {...(props as ComponentPropsWithoutRef<typeof motion.div>)}
    >
      {children}
    </motion.div>
  );
}

/**
 * MotionButton — spring tap feedback for CTA buttons.
 * Pairs with any button variant — wraps children, does not replace button semantics.
 */
interface MotionButtonWrapProps {
  children: ReactNode;
  disabled?: boolean;
  className?: string;
}

export function MotionPress({ children, disabled, className }: MotionButtonWrapProps) {
  return (
    <motion.span
      whileTap={disabled ? undefined : { scale: 0.96 }}
      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      className={cn('inline-flex', className)}
    >
      {children}
    </motion.span>
  );
}
